/* Hands-free driver automation — how aggressively GPS advances a docket when
   the driver can't be bothered pressing the buttons. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Satellite, Loader2 } from "lucide-react";

const FIELDS = [
  ["site_radius_m", "Site fence", "m", "How close counts as being at the site. Rural and lot-number addresses geocode loosely, so keep this generous."],
  ["onsite_stationary_s", "Parked before 'arrived'", "sec", "How long the truck must sit still inside the fence before the on-site timer starts."],
  ["onsite_speed_kph", "Counts as parked under", "km/h", "Ignore GPS jitter — anything slower than this is treated as stopped."],
  ["complete_away_m", "Left-site distance", "m", "How far clear of the site before we accept they've actually left, not just shuffled."],
  ["complete_prompt_s", "Wait before prompting", "sec", "How long after leaving before the driver gets the big 'Load delivered?' button."],
];

export default function DriverAutomationCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/driver-automation").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/driver-automation", cfg);
      setCfg(data);
      toast.success("Driver automation saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="border border-industrial bg-surface p-4" data-testid="driver-automation-card">
      <div className="flex items-start gap-3">
        <Satellite className="w-5 h-5 text-primary-orange shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Driver App</div>
          <h2 className="heading text-lg font-black uppercase mt-0.5">Hands-Free Status</h2>
          <p className="text-xs text-muted-industrial mt-1 leading-relaxed max-w-2xl">
            Works the docket out from the truck's GPS trail instead of waiting for the driver to press Depart and
            Arrived. Runs on the server, so locking the phone only delays it. A load is never closed automatically —
            leaving the site raises a single big "Load delivered?" button so the customer signature is still captured.
            Manual buttons always stay available.
          </p>

          <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
            <input type="checkbox" data-testid="driver-automation-enabled" checked={!!cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="accent-[rgb(var(--primary))] w-4 h-4" />
            <span className="text-xs text-ink font-bold">Advance dockets automatically from GPS</span>
          </label>
          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
            <input type="checkbox" data-testid="driver-automation-learn" checked={!!cfg.learn_sites}
              onChange={(e) => setCfg({ ...cfg, learn_sites: e.target.checked })}
              className="accent-[rgb(var(--primary))] w-4 h-4" />
            <span className="text-xs text-ink">Learn where trucks actually park, so repeat sites get more accurate</span>
          </label>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {FIELDS.map(([key, label, unit, help]) => (
              <div key={key} className="border border-industrial p-2.5">
                <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{label} ({unit})</div>
                <Input type="number" value={cfg[key] ?? ""} data-testid={`driver-automation-${key}`}
                  onChange={(e) => setCfg({ ...cfg, [key]: e.target.value === "" ? null : Number(e.target.value) })}
                  className="rounded-none bg-transparent border-industrial mono h-8 mt-1" />
                <div className="text-[10px] text-muted-industrial mt-1 leading-snug">{help}</div>
              </div>
            ))}
          </div>

          <Button onClick={save} disabled={saving} data-testid="driver-automation-save"
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs tracking-widest font-bold mt-3 h-9">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save automation"}
          </Button>
        </div>
      </div>
    </div>
  );
}
